import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import { useGroupDetail, useGroupDeepLinkState } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { HeaderBar } from './HeaderBar';
import { KpiStrip } from './KpiStrip';
import { TAB_IDS, tabElementId, tabPanelId } from './tabIds';
import { GroupTabBar } from './parts/GroupTabBar';
import { AlertsPanel } from './parts/AlertsPanel';
import {
  OverviewTab,
  StudentsTab,
  ScheduleTab,
  ZoomTab,
  PandaDocTab,
  MaterialsTab,
  CommunicationTab,
  TasksTab,
  FinancesTab,
} from './tabs';

/**
 * `/settings/groups/:id/details` — the «group control center».
 *
 * Full shape (see `~/.claude/plans/replicated-launching-quail.md`,
 * backend TZ in `docs/groups/backend_tz_group_card.md`):
 *   - HeaderBar with identity + status pills + primary actions
 *     (Add Student / Edit / SyncMenu / LMS toggle / Archive).
 *   - KpiStrip with 4–5 per-currency cards (capacity, lessons,
 *     revenue, debt, optional next-lesson).
 *   - AlertsPanel («Needs attention») surfacing actionable problems,
 *     each deep-linking into the relevant tab + filter.
 *   - A resilient, accessible `GroupTabBar` (overflow + keyboard nav).
 *   - Seven tabs: Overview / Students / Schedule / Zoom / PandaDoc /
 *     Materials / Finances. Each tab queries lazily on first visit
 *     to keep first-paint cheap; subsequent visits hit Apollo cache.
 *
 * All URL view-state (`?tab/?filter/?q/?sort/?kind`) is owned by
 * `useGroupDeepLinkState`, which parses/encodes the query string through
 * `history.replace`. The active tab comes from `state.tab` and `setTab`
 * switches it; `applyAlert` replaces (never merges) tab + roster filter from
 * a selected alert (Req 4.6/4.7).
 */
export const GroupDetail = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const { state, setTab, applyAlert } = useGroupDeepLinkState();
  const { group, loading, refetch } = useGroupDetail(id);

  if (loading && !group) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }
  if (!group) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.BackLink onClick={() => history.push(PATHS.settingsGroups)}>
            {t('pages.groupDetail.backToList')}
          </S.BackLink>
          <S.EmptyState>—</S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.BackLink onClick={() => history.push(PATHS.settingsGroups)}>
          {t('pages.groupDetail.backToList')}
        </S.BackLink>

        <HeaderBar group={group} onSynced={refetch} />
        <KpiStrip group={group} />

        <AlertsPanel groupId={group.id} onSelectAlert={applyAlert} />

        <GroupTabBar tabs={TAB_IDS} active={state.tab} onSelect={setTab} />

        <div
          role="tabpanel"
          id={tabPanelId(state.tab)}
          aria-labelledby={tabElementId(state.tab)}
        >
          {state.tab === 'overview' && (
            <OverviewTab group={group} onNavigate={setTab} />
          )}
          {state.tab === 'students' && (
            <StudentsTab group={group} onChanged={refetch} />
          )}
          {state.tab === 'schedule' && <ScheduleTab groupId={group.id} />}
          {state.tab === 'zoom' && <ZoomTab groupId={group.id} />}
          {state.tab === 'pandadoc' && <PandaDocTab groupId={group.id} />}
          {state.tab === 'materials' && <MaterialsTab groupId={group.id} />}
          {state.tab === 'communication' && (
            <CommunicationTab
              groupId={group.id}
              initialChannelId={state.conv || undefined}
            />
          )}
          {state.tab === 'tasks' && <TasksTab groupId={group.id} />}
          {state.tab === 'finances' && <FinancesTab group={group} />}
        </div>
      </S.Wrapper>
    </MainTemplate>
  );
});
